import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./StoreSignatureCard.module.scss";
import primaryComponents from "../../../primaryComponents";
import useSignature from "./useSignature";

const StoreSignatureCard = ({
  storeDetails,
  setStoreDetails,
  isEditMode,
  storeInfo,
  className = "",
  style = {},
}) => {
  const {
    activeTab,
    setActiveTab,
    isChanging,
    setIsChanging,
    isUploading,
    uploadText,
    isDragging,
    hasDrawn,
    fileInputRef,
    canvasRef,
    currentSignature,
    showInputArea,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    clearCanvas,
    handleSaveDrawn,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveSignature,
  } = useSignature({
    storeDetails,
    setStoreDetails,
    isEditMode,
    storeInfo,
  });

  return (
    <div className={className || styles.signatureCard} style={style}>
      <div>
        <h5 className="font-weight-bold">
          Store Signature (Invoice Signature)
        </h5>
        <p className="gray--text smallest-text-size mb-0">
          Upload an image of your signature or draw it electronically. This
          signature will appear on store invoices and receipts.
        </p>
      </div>

      <div className="d-flex flex-column flex-grow-1">
        {isUploading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 flex-grow-1">
            <FontAwesomeIcon
              icon="spinner"
              spin
              style={{ fontSize: "28px" }}
              className="primary--text mb-2"
            />
            <span className="gray--text small-text-size">
              {uploadText || "Uploading signature..."}
            </span>
          </div>
        ) : !showInputArea ? (
          /* Active Signature Preview */
          <div
            className="d-flex flex-column flex-grow-1"
            style={{ gap: "15px" }}
          >
            <div
              className={`${styles.previewBox} d-flex align-items-center justify-content-center p-3`}
            >
              <img
                src={currentSignature}
                alt="Store Invoice Signature"
                className={styles.previewImage}
              />
            </div>
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gap: "10px" }}
            >
              <primaryComponents.Button
                type="button"
                classNames="btn btn--outline smallBtn"
                style={{ width: "max-content", height: "36px" }}
                onClick={() => setIsChanging(true)}
              >
                <FontAwesomeIcon icon="pen" className="mr-2" />
                <span>Change Signature</span>
              </primaryComponents.Button>

              <primaryComponents.Button
                type="button"
                classNames="btn btn--outline-red smallBtn"
                style={{ width: "max-content", height: "36px" }}
                onClick={handleRemoveSignature}
              >
                <FontAwesomeIcon icon="trash-alt" className="mr-2" />
                <span>Remove Signature</span>
              </primaryComponents.Button>
            </div>
          </div>
        ) : (
          /* Upload or Draw Selection Area */
          <div
            className="d-flex flex-column flex-grow-1"
            style={{ gap: "12px" }}
          >
            <div
              className="d-flex p-1 rounded"
              style={{
                gap: "8px",
                background: "#f4f4f7",
                width: "fit-content",
              }}
            >
              <primaryComponents.Button
                type="button"
                className={`${styles.tabBtn} ${
                  activeTab === "upload" ? styles.activeTab : ""
                }`}
                onClick={() => setActiveTab("upload")}
              >
                <FontAwesomeIcon icon="upload" />
                <span>Upload File</span>
              </primaryComponents.Button>
              <primaryComponents.Button
                type="button"
                className={`${styles.tabBtn} ${
                  activeTab === "draw" ? styles.activeTab : ""
                }`}
                onClick={() => setActiveTab("draw")}
              >
                <FontAwesomeIcon icon="pen" />
                <span>Draw Signature</span>
              </primaryComponents.Button>
            </div>

            {activeTab === "upload" ? (
              <div
                className="d-flex flex-column flex-grow-1"
                style={{ gap: "12px" }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                  style={{ display: "none" }}
                />
                <div
                  className={`${styles.uploadDropzone} ${
                    isDragging ? styles.dragging : ""
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && fileInputRef.current?.click()
                  }
                >
                  <FontAwesomeIcon
                    icon="cloud-upload-alt"
                    className="primary--text mb-2"
                    style={{ fontSize: "28px" }}
                  />
                  <p className="font-weight-bold dark--text small-text-size mb-1">
                    Click or drag & drop signature image here
                  </p>
                  <p className="gray--text smallest-text-size mb-0">
                    PNG, JPG, or GIF accepted (Max 5MB)
                  </p>
                </div>
                {isChanging && (
                  <div>
                    <primaryComponents.Button
                      type="button"
                      classNames="btn btn--outline smallBtn"
                      style={{ width: "max-content", height: "36px" }}
                      onClick={() => setIsChanging(false)}
                    >
                      Cancel
                    </primaryComponents.Button>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="d-flex flex-column flex-grow-1"
                style={{ gap: "12px" }}
              >
                <div className={styles.canvasWrapper}>
                  <canvas
                    ref={canvasRef}
                    className={styles.signatureCanvas}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerLeave={handlePointerUp}
                  />
                  <div className={styles.signatureBaseline} />
                  <span
                    className="position-absolute gray--text smallest-text-size"
                    style={{
                      right: "16px",
                      bottom: "12px",
                      pointerEvents: "none",
                    }}
                  >
                    Sign above the line
                  </span>
                </div>

                <div
                  className="d-flex align-items-center justify-content-between flex-wrap"
                  style={{ gap: "10px" }}
                >
                  <div
                    className="d-flex align-items-center flex-wrap"
                    style={{ gap: "10px" }}
                  >
                    <primaryComponents.Button
                      type="button"
                      classNames="btn btn--outline smallBtn"
                      onClick={clearCanvas}
                      isDisabled={!hasDrawn}
                    >
                      <FontAwesomeIcon icon="eraser" className="mr-2" />
                      <span>Clear Pad</span>
                    </primaryComponents.Button>
                    {isChanging && (
                      <primaryComponents.Button
                        type="button"
                        classNames="btn btn--outline smallBtn"
                        onClick={() => setIsChanging(false)}
                      >
                        <span>Cancel</span>
                      </primaryComponents.Button>
                    )}
                  </div>

                  <div>
                    <primaryComponents.Button
                      type="button"
                      classNames="btn btn--primary smallBtn"
                      onClick={handleSaveDrawn}
                      isDisabled={!hasDrawn}
                    >
                      <FontAwesomeIcon icon="check" className="mr-2" />
                      <span>Apply</span>
                    </primaryComponents.Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreSignatureCard;
